import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRightLeft, ShieldPlus, ShieldOff, ShieldCheck, BadgeCheck, UserCog, KeyRound, Mail, CalendarClock, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import PasswordInput from "@/components/PasswordInput";
import DateField from "@/components/DateField";
import TimeOnlyField from "@/components/TimeOnlyField";
import { todayLocal, fmtDowOrd } from "@/lib/timefmt";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

export default function MembersScoresTab() {
  const { user: me, refresh } = useAuth();
  const nav = useNavigate();
  const canAssume = me && !me.impersonated_by && ((me.role === "admin" && !me.admin_level) || me.admin_level === "global" || (me.privilege || "user") === "command");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [tree, setTree] = useState({ units: [], transfer_team: null });
  const [terms, setTerms] = useState({ unit_label: "Unit", subunit_label: "Subunit", team_label: "Team" });
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ unit_id: "", subunit_id: "", team_id: "" });
  const [moveFor, setMoveFor] = useState(null);
  const [adminFor, setAdminFor] = useState(null);
  const [roleFor, setRoleFor] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [dutyFor, setDutyFor] = useState(null);
  const [privFor, setPrivFor] = useState(null);
  const [roles, setRoles] = useState([]);
  const [posOptions, setPosOptions] = useState([]);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [qf, setQf] = useState({ name: "", extid: "", position: "", placement: "", admin: "" });

  const setPosition = async (m, position) => {
    try {
      await api.post(`/admin/users/${m.id}/position`, { position: position || null });
      setMembers((ms) => ms.map((x) => (x.id === m.id ? { ...x, position: position || null } : x)));
      toast.success(position ? `Position set to ${position}` : "Position cleared");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleAuthOfficer = async (m) => {
    try {
      await api.put(`/admin/users/${m.id}/auth-officer`, { auth_officer: !m.auth_officer });
      setMembers((ms) => ms.map((x) => (x.id === m.id ? { ...x, auth_officer: !m.auth_officer } : x)));
      toast.success(m.auth_officer ? `Auth Officer removed for ${m.first_name}` : `${m.first_name} is now an Auth Officer`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (r.data[0]) setCompanyId((prev) => prev || r.data[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTree = () => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => {
      setTree({ units: r.data.units, transfer_team: r.data.transfer_team });
      setTerms(r.data.terminology);
    }).catch(() => {});
  };

  const loadMembers = () => {
    if (!companyId) return;
    const params = { company_id: companyId };
    if (filters.team_id) params.team_id = filters.team_id;
    else if (filters.subunit_id) params.subunit_id = filters.subunit_id;
    else if (filters.unit_id) params.unit_id = filters.unit_id;
    api.get("/org/members", { params }).then((r) => setMembers(r.data.members))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  const sendReset = async (m) => {
    if (!window.confirm(`Email a password reset link to ${m.first_name} ${m.last_name}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${m.id}/reset-link`);
      toast.success(`Reset link sent to ${data.sent_to.join(", ")}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assumeIdentity = async (m) => {
    if (!window.confirm(`Assume the identity of ${m.first_name} ${m.last_name}? You will see Crew Ready exactly as they do — any changes you make apply to their data — until you return to your account.`)) return;
    try {
      await api.post(`/admin/users/${m.id}/impersonate`);
      await refresh();
      nav("/");
      toast.success(`Now viewing as ${m.first_name} ${m.last_name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    loadTree();
    if (companyId) {
      api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
      api.get(`/position-config?company_id=${companyId}`).then((r) => setPosOptions(r.data.options || [])).catch(() => {});
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadMembers(); }, [companyId, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const names = useMemo(() => {
    const m = {};
    (tree.units || []).forEach((u) => {
      m[u.id] = u.name;
      (u.subunits || []).forEach((s) => {
        m[s.id] = s.name;
        (s.teams || []).forEach((t) => { m[t.id] = t.name; });
      });
    });
    if (tree.transfer_team) m[tree.transfer_team.id] = tree.transfer_team.name;
    return m;
  }, [tree]);

  const orgPath = (m) => {
    const org = m.org || {};
    if (!org.team_id) return "Unassigned";
    if (tree.transfer_team && org.team_id === tree.transfer_team.id) return tree.transfer_team.name;
    return [names[org.unit_id], names[org.subunit_id], names[org.team_id]].filter(Boolean).join(" / ");
  };

  const allSubunits = (tree.units || []).flatMap((u) => u.subunits || []);
  const allTeams = allSubunits.flatMap((s) => s.teams || []);
  const roleName = (m) => roles.find((r) => r.id === m.role_id)?.name || "—";

  const memberText = (m) => ({
    name: `${m.first_name || ""} ${m.last_name || ""} ${m.email || ""} ${roleName(m)}`,
    extid: m.external_user_id || "",
    position: m.position || "",
    placement: orgPath(m),
    admin: m.admin_level ? `${m.admin_level} admin` : "",
  });
  const memberKey = (m, k) => memberText(m)[k] || "";
  const shown = sortRows(filterRows(members, memberText, qf), sort, memberKey);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setFilters({ unit_id: "", subunit_id: "", team_id: "" }); }}
            data-testid="members-company-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">{terms.unit_label}</label>
          <select value={filters.unit_id} onChange={(e) => setFilters({ unit_id: e.target.value, subunit_id: "", team_id: "" })}
            data-testid="members-filter-unit"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {(tree.units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">{terms.subunit_label}</label>
          <select value={filters.subunit_id} onChange={(e) => setFilters({ ...filters, subunit_id: e.target.value, team_id: "" })}
            data-testid="members-filter-subunit"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {allSubunits.filter((s) => !filters.unit_id || s.unit_id === filters.unit_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">{terms.team_label}</label>
          <select value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}
            data-testid="members-filter-team"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {allTeams.filter((t) => !filters.subunit_id || t.subunit_id === filters.subunit_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            {tree.transfer_team && <option value={tree.transfer_team.id}>{tree.transfer_team.name}</option>}
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="members-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="Member" k="name" sort={sort} setSort={setSort} />
                <SortTh label="Ext ID" k="extid" sort={sort} setSort={setSort} />
                <SortTh label="Position" k="position" sort={sort} setSort={setSort} />
                <SortTh label="Placement" k="placement" sort={sort} setSort={setSort} />
                <SortTh label="Admin" k="admin" sort={sort} setSort={setSort} />
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num text-right">Actions</th>
              </tr>
              <tr className="border-b border-slate-800 text-left">
                <FilterTh value={qf.name} onChange={(v) => setQf((f) => ({ ...f, name: v }))} testId="members-qfilter-name" />
                <FilterTh value={qf.extid} onChange={(v) => setQf((f) => ({ ...f, extid: v }))} testId="members-qfilter-extid" />
                <FilterTh value={qf.position} onChange={(v) => setQf((f) => ({ ...f, position: v }))} testId="members-qfilter-position" />
                <FilterTh value={qf.placement} onChange={(v) => setQf((f) => ({ ...f, placement: v }))} testId="members-qfilter-placement" />
                <FilterTh value={qf.admin} onChange={(v) => setQf((f) => ({ ...f, admin: v }))} testId="members-qfilter-admin" />
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No members found.</td></tr>
              )}
              {shown.map((m) => {
                return (
                  <tr key={m.id} className="hover:bg-slate-900/60" data-testid={`member-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{`${m.first_name} ${m.last_name}`.trim() || m.email}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num uppercase tracking-wider mt-0.5">{roleName(m)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono-num text-xs text-slate-400">{m.external_user_id}</td>
                    <td className="px-4 py-3">
                      {posOptions.length ? (
                        <select value={m.position || ""} onChange={(e) => setPosition(m, e.target.value)}
                          data-testid={`member-position-${m.id}`}
                          className="px-2 py-1.5 rounded-md bg-slate-950 border border-slate-800 text-xs">
                          <option value="">—</option>
                          {posOptions.map((p) => <option key={p.name} value={p.name}>{p.name} · {p.value}</option>)}
                        </select>
                      ) : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-300">{orgPath(m)}</td>
                    <td className="px-4 py-3">
                      {m.admin_level
                        ? <span className="px-2 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-600/40 text-[10px] uppercase tracking-widest font-mono-num">{m.admin_level} admin</span>
                        : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setMoveFor(m)} data-testid={`member-move-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <ArrowRightLeft className="h-3 w-3" /> Move
                      </button>
                      {canAssume && m.id !== me.id && (
                        <button onClick={() => assumeIdentity(m)} data-testid={`member-assume-${m.id}`}
                          title="Assume this member's identity to view and fix their data"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-600/40 text-xs mr-2">
                          <Eye className="h-3 w-3" /> Assume
                        </button>
                      )}
                      <button onClick={() => setRoleFor(m)} data-testid={`member-role-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <UserCog className="h-3 w-3" /> Role
                      </button>
                      <button onClick={() => setPwFor(m)} data-testid={`member-password-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <KeyRound className="h-3 w-3" /> Password
                      </button>
                      <button onClick={() => sendReset(m)} data-testid={`member-reset-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <Mail className="h-3 w-3" /> Reset Link
                      </button>
                      <button onClick={() => setDutyFor(m)} data-testid={`member-duty-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <CalendarClock className="h-3 w-3" /> Duty
                      </button>
                      <button onClick={() => setPrivFor(m)} data-testid={`member-privilege-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs mr-2">
                        <ShieldCheck className="h-3 w-3" /> Privilege
                      </button>
                      <button onClick={() => toggleAuthOfficer(m)} data-testid={`member-auth-officer-${m.id}`}
                        title="Auth Officer — can authorise tasks flagged 'Requires authorisation' (separate from role/privilege)"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs mr-2 transition-colors ${m.auth_officer ? "bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-600/40" : "bg-slate-800 hover:bg-slate-700"}`}>
                        <BadgeCheck className="h-3 w-3" /> Auth
                      </button>
                      <button onClick={() => setAdminFor(m)} data-testid={`member-admin-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-600/40 text-xs">
                        {m.admin_level ? <ShieldOff className="h-3 w-3" /> : <ShieldPlus className="h-3 w-3" />} Admin
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <MoveDialog target={moveFor} tree={tree} terms={terms} companyId={companyId} onClose={() => setMoveFor(null)} onDone={() => { setMoveFor(null); loadMembers(); }} />
      <AdminDialog target={adminFor} tree={tree} terms={terms} companyId={companyId} canGlobal={!!me && ((me.role === "admin" && !me.admin_level) || me.admin_level === "global")} onClose={() => setAdminFor(null)} onDone={() => { setAdminFor(null); loadMembers(); }} />
      <RoleDialog target={roleFor} roles={roles} onClose={() => setRoleFor(null)} onDone={() => { setRoleFor(null); loadMembers(); }} />
      <PasswordDialog target={pwFor} onClose={() => setPwFor(null)} onDone={() => setPwFor(null)} />
      <DutyPatternDialog target={dutyFor} companyId={companyId} onClose={() => setDutyFor(null)} onDone={() => setDutyFor(null)} />
      <PrivilegeDialog target={privFor} onClose={() => setPrivFor(null)} onDone={() => { setPrivFor(null); loadMembers(); }} />
    </div>
  );
}

function MoveDialog({ target, tree, terms, companyId, onClose, onDone }) {
  const [dest, setDest] = useState("");
  useEffect(() => { setDest(""); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const options = [];
  for (const u of tree.units || []) {
    options.push({ value: `unit:${u.id}`, label: `${u.name} — ${terms.unit_label} level` });
    for (const s of u.subunits || []) {
      options.push({ value: `subunit:${s.id}`, label: `${u.name} / ${s.name} — ${terms.subunit_label} level` });
      for (const t of s.teams || []) {
        options.push({ value: `team:${t.id}`, label: `${u.name} / ${s.name} / ${t.name}` });
      }
    }
  }
  const submit = async () => {
    if (!dest) { toast.error("Pick a destination"); return; }
    const [kind, id] = dest.split(":");
    const payload = kind === "company" ? { company_id: companyId } : { [`${kind}_id`]: id };
    try {
      await api.post(`/admin/users/${target.id}/move`, payload);
      toast.success("Member moved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Move {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Destination in org tree</label>
        <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="move-dest-select"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="">Select…</option>
          <option value="company">Company level (no {terms.unit_label.toLowerCase()})</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          {tree.transfer_team && <option value={`team:${tree.transfer_team.id}`}>{tree.transfer_team.name} (transfer)</option>}
        </select>
        <button onClick={submit} data-testid="move-confirm-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Move member</button>
      </DialogContent>
    </Dialog>
  );
}

function AdminDialog({ target, tree, terms, companyId, canGlobal, onClose, onDone }) {
  const [level, setLevel] = useState("");
  const [scopeId, setScopeId] = useState("");
  useEffect(() => {
    setLevel(target?.admin_level || "");
    setScopeId(target?.admin_scope_id || "");
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const units = tree.units || [];
  const subunits = units.flatMap((u) => u.subunits || []);
  const teams = subunits.flatMap((s) => s.teams || []);
  const scopeOptions =
    level === "global" ? []
    : level === "company" ? [{ id: companyId, name: "This company" }]
    : level === "unit" ? units.map((u) => ({ id: u.id, name: u.name }))
    : level === "subunit" ? subunits.map((s) => ({ id: s.id, name: s.name }))
    : level === "team" ? [...teams.map((t) => ({ id: t.id, name: t.name })), ...(tree.transfer_team ? [{ id: tree.transfer_team.id, name: `${tree.transfer_team.name} (transfer)` }] : [])]
    : [];
  const submit = async () => {
    try {
      await api.post(`/admin/users/${target.id}/assign-admin`, {
        admin_level: level || null,
        scope_id: level && level !== "global" ? scopeId : null,
      });
      toast.success(level ? `Assigned ${level} admin` : "Admin rights removed");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Admin rights — {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Admin level</label>
        <select value={level} onChange={(e) => { setLevel(e.target.value); setScopeId(""); }} data-testid="admin-level-select"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          <option value="">Not an admin</option>
          {canGlobal && <option value="global">Global admin (all companies)</option>}
          <option value="company">Company admin (all levels)</option>
          <option value="unit">{terms.unit_label} admin</option>
          <option value="subunit">{terms.subunit_label} admin</option>
          <option value="team">{terms.team_label} admin</option>
        </select>
        {level && level !== "global" && (
          <>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Scope</label>
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="admin-scope-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">Select…</option>
              {scopeOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </>
        )}
        <button onClick={submit} data-testid="admin-assign-confirm-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save</button>
      </DialogContent>
    </Dialog>
  );
}

function RoleDialog({ target, roles, onClose, onDone }) {
  const [roleId, setRoleId] = useState("");
  useEffect(() => { setRoleId(target?.role_id || ""); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const submit = async () => {
    if (!roleId) { toast.error("Pick a role"); return; }
    try {
      await api.post(`/admin/users/${target.id}/role`, { role_id: roleId });
      toast.success("Role updated");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Change role — {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company role</label>
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)} data-testid="role-select"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="">Select…</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button onClick={submit} data-testid="role-confirm-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save role</button>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  useEffect(() => { setPw(""); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const submit = async () => {
    if (pw.length < 6) { toast.error("At least 6 characters"); return; }
    try {
      await api.post(`/admin/users/${target.id}/password`, { new_password: pw });
      toast.success(`Password updated for ${target.first_name}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Set password — {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">New password</label>
        <PasswordInput testId="admin-new-password" value={pw} onChange={(e) => setPw(e.target.value)}
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
        <button onClick={submit} data-testid="admin-password-confirm-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Set password</button>
      </DialogContent>
    </Dialog>
  );
}

const PRIVILEGE_OPTIONS = [
  { value: "user", label: "User", desc: "Standard member access" },
  { value: "supervisor", label: "Supervisor", desc: "Can view the Crew Board and move personnel within their org scope" },
  { value: "command", label: "Command", desc: "Full Crew Board visibility and personnel movement across the company" },
];

function PrivilegeDialog({ target, onClose, onDone }) {
  const [priv, setPriv] = useState("user");
  useEffect(() => { setPriv(target?.privilege || "user"); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const submit = async () => {
    try {
      await api.put(`/admin/users/${target.id}/privilege`, { privilege: priv });
      toast.success(`Privilege set to ${priv}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="privilege-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Privilege — {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {PRIVILEGE_OPTIONS.map((o) => (
            <button key={o.value} onClick={() => setPriv(o.value)} data-testid={`privilege-option-${o.value}`}
              className={`w-full text-left px-3 py-2.5 rounded-md border transition-colors ${
                priv === o.value ? "border-blue-500 bg-blue-600/15" : "border-slate-800 bg-slate-950 hover:border-slate-700"
              }`}>
              <div className="text-sm font-medium">{o.label}</div>
              <div className="text-[11px] text-slate-500">{o.desc}</div>
            </button>
          ))}
        </div>
        <button onClick={submit} data-testid="privilege-confirm-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save privilege</button>
      </DialogContent>
    </Dialog>
  );
}

const DOW_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

function DutyPatternDialog({ target, companyId, onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [anchor, setAnchor] = useState(todayLocal());
  const [numDays, setNumDays] = useState(7);
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkStart, setBulkStart] = useState("08:00");
  const [bulkEnd, setBulkEnd] = useState("17:00");

  useEffect(() => {
    if (!target) return;
    setSelected(new Set());
    setDays([]);
    setTemplateId("");
    setAnchor(todayLocal());
    api.get(`/admin/duty-templates?company_id=${companyId}`).then((r) => setTemplates(r.data)).catch(() => {});
    api.get(`/admin/duty-patterns/${target.id}`).then((r) => {
      if (r.data.pattern) {
        const p = r.data.pattern;
        setTemplateId(p.template_id || "");
        setAnchor(p.anchor_date);
        setNumDays(p.days.length);
        setDays(p.days);
      } else if (r.data.template) {
        setTemplateId(r.data.template.id);
        setBulkStart(r.data.template.duty_start);
        setBulkEnd(r.data.template.duty_end);
        setDays([]);
      }
    }).catch(() => {});
  }, [target, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDays((prev) => Array.from({ length: numDays }, (_, i) => prev[i] || { duty: true, start: bulkStart, end: bulkEnd }));
  }, [numDays, target]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!target) return null;

  const pickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setBulkStart(t.duty_start);
      setBulkEnd(t.duty_end);
      if (t.days?.length) {
        setNumDays(t.days.length);
        setDays(t.days.map((d) => ({ duty: !!d.duty, start: d.start || t.duty_start, end: d.end || t.duty_end })));
      } else {
        setDays((prev) => prev.map((d) => (d.duty ? { ...d, start: t.duty_start, end: t.duty_end } : d)));
      }
    }
  };

  const toggleSel = (i) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const setDuty = (duty) => setDays((prev) => prev.map((d, i) => selected.has(i) ? { ...d, duty, ...(duty ? { start: bulkStart, end: bulkEnd } : {}) } : d));

  const save = async () => {
    if (days.some((d) => d.duty && (!d.start || !d.end))) { toast.error("All duty days need start and end times"); return; }
    try {
      await api.post("/admin/duty-patterns", {
        user_id: target.id, template_id: templateId || null, anchor_date: anchor, days,
      });
      toast.success(`Duty pattern saved for ${target.first_name}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dayLabel = (i) => {
    const d = new Date(`${anchor}T00:00:00`);
    d.setDate(d.getDate() + i);
    return { dow: DOW_LETTERS[d.getDay()], num: d.getDate(), iso: d.toISOString().slice(0, 10) };
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duty-pattern-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Duty pattern — {target.first_name} {target.last_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Template</label>
            <select value={templateId} onChange={(e) => pickTemplate(e.target.value)} data-testid="duty-template-select"
              className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">Custom / none</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.duty_start}–{t.duty_end})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Pattern days (1–30)</label>
            <input type="number" min={1} max={30} value={numDays}
              onChange={(e) => setNumDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
              data-testid="duty-num-days"
              className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">First date of pattern</label>
            <DateField value={anchor} onChange={setAnchor} testId="duty-anchor-date" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 p-3 rounded-md border border-slate-800 bg-slate-950/40">
          <TimeOnlyField label="Duty start" testId="duty-bulk-start" value={bulkStart} onChange={setBulkStart} />
          <TimeOnlyField label="Duty end" testId="duty-bulk-end" value={bulkEnd} onChange={setBulkEnd} />
          <button onClick={() => setDuty(true)} data-testid="duty-set-duty"
            className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium">Set selected → Duty</button>
          <button onClick={() => setDuty(false)} data-testid="duty-set-free"
            className="px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-xs font-medium">Set selected → Free</button>
          <span className="text-[11px] text-slate-500">{selected.size} selected — click boxes to select</span>
        </div>
        <div className="grid grid-cols-7 gap-1.5 mt-4" data-testid="duty-grid">
          {days.map((d, i) => {
            const lbl = dayLabel(i);
            const sel = selected.has(i);
            return (
              <button key={lbl.iso} onClick={() => toggleSel(i)} data-testid={`duty-day-${i}`}
                className={`rounded-md border p-1.5 text-center transition-colors ${
                  sel ? "ring-2 ring-blue-500/70" : ""
                } ${d.duty ? "bg-blue-600/15 border-blue-600/40 text-blue-200" : "bg-slate-950/50 border-slate-800 text-slate-500"}`}>
                <div className="text-[9px] font-mono-num uppercase tracking-widest opacity-70">{lbl.dow}</div>
                <div className="font-mono-num text-sm font-semibold">{lbl.num}</div>
                <div className="text-[9px] font-mono-num mt-0.5">{d.duty ? `${d.start}-${d.end}` : "FREE"}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">The pattern repeats from the first date, cycling through the grid.</p>
        <button onClick={save} data-testid="duty-pattern-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save duty pattern</button>
      </DialogContent>
    </Dialog>
  );
}
